import type Hls from "hls.js";
import type { ErrorData } from "hls.js";
import {
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  findSlideIndex,
  formatClock,
  formatDurationLabel,
  loadSavedProgress,
  MANIFEST_URL,
  saveProgress,
  validateCourseManifest,
} from "./course";
import type {
  CourseManifest,
  SavedCourseProgress,
} from "./types";

type CourseView = "overview" | "player" | "complete";

const PLAYBACK_SPEEDS = [0.75, 1, 1.25, 1.5, 2] as const;

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return (
    target.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT", "BUTTON"].includes(target.tagName)
  );
}

function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(Math.max(value, minimum), maximum);
}

function App() {
  const initialProgressRef = useRef<SavedCourseProgress | null>(
    loadSavedProgress(),
  );
  const [course, setCourse] = useState<CourseManifest | null>(null);
  const [courseError, setCourseError] = useState<string | null>(null);
  const [manifestAttempt, setManifestAttempt] = useState(0);
  const [mediaAttempt, setMediaAttempt] = useState(0);
  const [view, setView] = useState<CourseView>("overview");
  const [mediaReady, setMediaReady] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(
    initialProgressRef.current?.volume ?? 1,
  );
  const [muted, setMuted] = useState(
    initialProgressRef.current?.muted ?? false,
  );
  const [playbackRate, setPlaybackRate] = useState(
    initialProgressRef.current?.playbackRate ?? 1,
  );
  const [captionsEnabled, setCaptionsEnabled] = useState(
    initialProgressRef.current?.captionsEnabled ?? false,
  );
  const [transcriptOpen, setTranscriptOpen] = useState(
    initialProgressRef.current?.transcriptOpen ?? true,
  );
  const [transcriptQuery, setTranscriptQuery] = useState("");
  const [savedProgress, setSavedProgress] =
    useState<SavedCourseProgress | null>(initialProgressRef.current);
  const [completed, setCompleted] = useState(
    initialProgressRef.current?.completed ?? false,
  );
  const [liveMessage, setLiveMessage] = useState(
    "Course overview available.",
  );

  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const playerShellRef = useRef<HTMLDivElement>(null);
  const overviewHeadingRef = useRef<HTMLHeadingElement>(null);
  const playerHeadingRef = useRef<HTMLHeadingElement>(null);
  const completionHeadingRef = useRef<HTMLHeadingElement>(null);
  const activeTranscriptRef = useRef<HTMLElement>(null);
  const lastAnnouncedSlideRef = useRef<number | null>(null);
  const lastSavedAtRef = useRef(0);
  const hasMountedViewRef = useRef(false);
  const liveMessageTimerRef = useRef<number | null>(null);

  const announce = useCallback((message: string) => {
    if (liveMessageTimerRef.current !== null) {
      window.clearTimeout(liveMessageTimerRef.current);
    }
    setLiveMessage("");
    liveMessageTimerRef.current = window.setTimeout(() => {
      setLiveMessage(message);
    }, 25);
  }, []);

  useEffect(
    () => () => {
      if (liveMessageTimerRef.current !== null) {
        window.clearTimeout(liveMessageTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const controller = new AbortController();

    async function loadCourse() {
      setCourseError(null);
      try {
        const response = await fetch(MANIFEST_URL, {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(
            `The course manifest returned HTTP ${response.status}.`,
          );
        }

        const manifest = validateCourseManifest(await response.json());
        setCourse(manifest);
        setDuration(manifest.totalDurationMs / 1000);
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }
        setCourse(null);
        setCourseError(
          error instanceof Error
            ? error.message
            : "The course manifest could not be loaded.",
        );
      }
    }

    void loadCourse();
    return () => controller.abort();
  }, [manifestAttempt]);

  useEffect(() => {
    const video = videoRef.current;
    if (!course || !video) {
      return;
    }

    setMediaReady(false);
    setMediaError(null);
    setIsBuffering(false);
    let disposed = false;

    const markReady = () => {
      if (!disposed) {
        setMediaReady(true);
        setMediaError(null);
      }
    };

    let localHls: Hls | null = null;

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = course.continuous.hls;
      video.addEventListener("loadedmetadata", markReady, { once: true });
      video.load();
    } else {
      // The light build is bundled locally and is sufficient for muxed VOD
      // playback; captions are supplied by the explicit WebVTT track below.
      void import("hls.js/light")
        .then(({ default: HlsController }) => {
          if (disposed) {
            return;
          }
          if (!HlsController.isSupported()) {
            setMediaError(
              "This browser does not support HLS playback. Use a current version of Safari, Chrome, Firefox, or Edge.",
            );
            return;
          }

          const hls = new HlsController({
            enableWorker: true,
            backBufferLength: 90,
            maxBufferLength: 45,
            capLevelToPlayerSize: true,
          });
          localHls = hls;
          hlsRef.current = hls;
          hls.on(HlsController.Events.MEDIA_ATTACHED, () => {
            hls.loadSource(course.continuous.hls);
          });
          hls.on(HlsController.Events.MANIFEST_PARSED, markReady);
          hls.on(
            HlsController.Events.ERROR,
            (_event, data: ErrorData) => {
              if (!data.fatal || disposed) {
                return;
              }

              const reason =
                data.type === HlsController.ErrorTypes.NETWORK_ERROR
                  ? "The training stream could not be reached."
                  : data.type === HlsController.ErrorTypes.MEDIA_ERROR
                    ? "The browser could not decode the training stream."
                    : "The training player could not initialise.";
              setMediaError(
                `${reason} Check the connection and try again.`,
              );
              setMediaReady(false);
              setIsBuffering(false);
            },
          );
          hls.attachMedia(video);
        })
        .catch(() => {
          if (!disposed) {
            setMediaError(
              "The HLS player could not be loaded. Refresh the page or use another current browser.",
            );
          }
        });
    }

    return () => {
      disposed = true;
      video.removeEventListener("loadedmetadata", markReady);
      localHls?.destroy();
      hlsRef.current = null;
      video.pause();
      video.removeAttribute("src");
      video.load();
    };
  }, [course, mediaAttempt]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    video.volume = volume;
    video.muted = muted;
    video.playbackRate = playbackRate;
  }, [mediaReady, muted, playbackRate, volume]);

  const setCaptionMode = useCallback((enabled: boolean) => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    for (const track of Array.from(video.textTracks)) {
      track.mode = enabled ? "showing" : "disabled";
    }
  }, []);

  const syncCaptionMode = useCallback(() => {
    setCaptionMode(captionsEnabled);
  }, [captionsEnabled, setCaptionMode]);

  useEffect(() => {
    syncCaptionMode();
  }, [syncCaptionMode, course]);

  const effectiveDuration =
    duration > 0 ? duration : (course?.totalDurationMs ?? 0) / 1000;
  const currentSlideIndex = useMemo(
    () => (course ? findSlideIndex(course.slides, currentTime) : 0),
    [course, currentTime],
  );
  const currentSlide = course?.slides[currentSlideIndex] ?? null;
  const progressPercent =
    effectiveDuration > 0
      ? clamp((currentTime / effectiveDuration) * 100, 0, 100)
      : 0;

  const hasResume =
    !!savedProgress &&
    !savedProgress.completed &&
    savedProgress.currentTime >= 1 &&
    savedProgress.currentTime < effectiveDuration - 2;

  const filteredSlides = useMemo(() => {
    if (!course) {
      return [];
    }
    const query = transcriptQuery.trim().toLocaleLowerCase();
    if (!query) {
      return course.slides;
    }
    return course.slides.filter(
      (slide) =>
        slide.title.toLocaleLowerCase().includes(query) ||
        slide.module.toLocaleLowerCase().includes(query) ||
        slide.transcript.toLocaleLowerCase().includes(query),
    );
  }, [course, transcriptQuery]);

  const persistProgress = useCallback(
    (overrides: Partial<SavedCourseProgress> = {}) => {
      const video = videoRef.current;
      const next: SavedCourseProgress = {
        version: 1,
        currentTime:
          overrides.currentTime ?? video?.currentTime ?? currentTime,
        playbackRate: overrides.playbackRate ?? playbackRate,
        volume: overrides.volume ?? volume,
        muted: overrides.muted ?? muted,
        captionsEnabled:
          overrides.captionsEnabled ?? captionsEnabled,
        transcriptOpen: overrides.transcriptOpen ?? transcriptOpen,
        completed: overrides.completed ?? completed,
        updatedAt: new Date().toISOString(),
      };
      saveProgress(next);
      setSavedProgress(next);
    },
    [
      captionsEnabled,
      completed,
      currentTime,
      muted,
      playbackRate,
      transcriptOpen,
      volume,
    ],
  );

  useEffect(() => {
    const saveBeforeExit = () => persistProgress();
    window.addEventListener("beforeunload", saveBeforeExit);
    return () => window.removeEventListener("beforeunload", saveBeforeExit);
  }, [persistProgress]);

  useEffect(() => {
    if (!currentSlide || view !== "player") {
      return;
    }
    if (lastAnnouncedSlideRef.current === currentSlide.id) {
      return;
    }

    lastAnnouncedSlideRef.current = currentSlide.id;
    announce(
      `Slide ${currentSlide.id} of ${course?.totalSlides}: ${currentSlide.title}. ${currentSlide.module}.`,
    );
  }, [announce, course?.totalSlides, currentSlide, view]);

  useEffect(() => {
    if (
      view !== "player" ||
      !transcriptOpen ||
      transcriptQuery.trim() ||
      !activeTranscriptRef.current
    ) {
      return;
    }

    activeTranscriptRef.current.scrollIntoView({
      block: "nearest",
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  }, [currentSlideIndex, transcriptOpen, transcriptQuery, view]);

  useEffect(() => {
    if (!hasMountedViewRef.current) {
      hasMountedViewRef.current = true;
      return;
    }

    window.requestAnimationFrame(() => {
      if (view === "overview") {
        overviewHeadingRef.current?.focus();
      } else if (view === "player") {
        playerHeadingRef.current?.focus();
      } else {
        completionHeadingRef.current?.focus();
      }
    });
  }, [view]);

  const startPlayback = useCallback(
    async (startTime: number, resetCompletion = false) => {
      const video = videoRef.current;
      if (!video || !mediaReady || mediaError) {
        return;
      }

      const targetTime = clamp(
        startTime,
        0,
        Math.max(effectiveDuration - 0.1, 0),
      );
      video.currentTime = targetTime;
      setCurrentTime(targetTime);
      if (resetCompletion) {
        setCompleted(false);
        persistProgress({ currentTime: targetTime, completed: false });
      }
      setView("player");

      try {
        await video.play();
      } catch {
        setMediaError(
          "Playback was blocked by the browser. Select Play to continue.",
        );
      }
    },
    [effectiveDuration, mediaError, mediaReady, persistProgress],
  );

  const togglePlayback = useCallback(async () => {
    const video = videoRef.current;
    if (!video || !mediaReady) {
      return;
    }

    if (video.paused) {
      try {
        await video.play();
      } catch {
        setMediaError(
          "Playback was blocked by the browser. Select Play again to continue.",
        );
      }
    } else {
      video.pause();
    }
  }, [mediaReady]);

  const seekTo = useCallback(
    (nextTime: number) => {
      const video = videoRef.current;
      if (!video) {
        return;
      }
      const bounded = clamp(nextTime, 0, effectiveDuration);
      video.currentTime = bounded;
      setCurrentTime(bounded);
      persistProgress({ currentTime: bounded });
    },
    [effectiveDuration, persistProgress],
  );

  const skipBy = useCallback(
    (seconds: number) => {
      seekTo((videoRef.current?.currentTime ?? currentTime) + seconds);
    },
    [currentTime, seekTo],
  );

  const changeVolume = useCallback(
    (nextVolume: number) => {
      const video = videoRef.current;
      const bounded = clamp(nextVolume, 0, 1);
      setVolume(bounded);
      if (video) {
        video.volume = bounded;
      }
      if (bounded > 0 && muted) {
        setMuted(false);
        if (video) {
          video.muted = false;
        }
      }
      persistProgress({
        volume: bounded,
        muted: bounded > 0 ? false : muted,
      });
    },
    [muted, persistProgress],
  );

  const toggleMute = useCallback(() => {
    const video = videoRef.current;
    const nextMuted = !muted;
    setMuted(nextMuted);
    if (video) {
      video.muted = nextMuted;
    }
    persistProgress({ muted: nextMuted });
    announce(nextMuted ? "Audio muted." : "Audio unmuted.");
  }, [announce, muted, persistProgress]);

  const changePlaybackRate = useCallback(
    (nextRate: number) => {
      const video = videoRef.current;
      setPlaybackRate(nextRate);
      if (video) {
        video.playbackRate = nextRate;
      }
      persistProgress({ playbackRate: nextRate });
      announce(`Playback speed ${nextRate} times.`);
    },
    [announce, persistProgress],
  );

  const toggleCaptions = useCallback(() => {
    const nextEnabled = !captionsEnabled;
    setCaptionsEnabled(nextEnabled);
    persistProgress({ captionsEnabled: nextEnabled });
    setCaptionMode(nextEnabled);
    announce(nextEnabled ? "Captions on." : "Captions off.");
  }, [
    announce,
    captionsEnabled,
    persistProgress,
    setCaptionMode,
  ]);

  const toggleTranscript = useCallback(() => {
    const nextOpen = !transcriptOpen;
    setTranscriptOpen(nextOpen);
    persistProgress({ transcriptOpen: nextOpen });
    announce(nextOpen ? "Transcript opened." : "Transcript closed.");
  }, [announce, persistProgress, transcriptOpen]);

  const toggleFullscreen = useCallback(async () => {
    const shell = playerShellRef.current;
    if (!shell) {
      return;
    }

    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        announce("Fullscreen closed.");
      } else if (shell.requestFullscreen) {
        await shell.requestFullscreen();
        announce("Fullscreen opened.");
      } else {
        const video = videoRef.current as
          | (HTMLVideoElement & { webkitEnterFullscreen?: () => void })
          | null;
        video?.webkitEnterFullscreen?.();
      }
    } catch {
      announce("Fullscreen is not available in this browser.");
    }
  }, [announce]);

  const restartTraining = useCallback(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    setCompleted(false);
    video.currentTime = 0;
    setCurrentTime(0);
    persistProgress({ currentTime: 0, completed: false });
    announce("Training restarted at slide 1.");
    void video.play().catch(() => {
      setMediaError(
        "Playback was blocked by the browser. Select Play to continue.",
      );
    });
  }, [announce, persistProgress]);

  const returnToOverview = useCallback(() => {
    videoRef.current?.pause();
    persistProgress();
    setView("overview");
    announce("Course overview.");
  }, [announce, persistProgress]);

  useEffect(() => {
    if (view !== "player") {
      return;
    }

    const handleShortcut = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        isTypingTarget(event.target)
      ) {
        return;
      }

      let handled = true;
      switch (event.code) {
        case "Space":
          void togglePlayback();
          break;
        case "ArrowLeft":
          skipBy(-10);
          break;
        case "ArrowRight":
          skipBy(10);
          break;
        case "ArrowUp":
          changeVolume(volume + 0.05);
          break;
        case "ArrowDown":
          changeVolume(volume - 0.05);
          break;
        case "KeyM":
          toggleMute();
          break;
        case "KeyC":
          toggleCaptions();
          break;
        case "KeyF":
          void toggleFullscreen();
          break;
        case "KeyR":
          restartTraining();
          break;
        default:
          handled = false;
      }

      if (handled) {
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", handleShortcut);
    return () => document.removeEventListener("keydown", handleShortcut);
  }, [
    changeVolume,
    restartTraining,
    skipBy,
    toggleCaptions,
    toggleFullscreen,
    toggleMute,
    togglePlayback,
    view,
    volume,
  ]);

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    setCurrentTime(video.currentTime);
    const now = Date.now();
    if (now - lastSavedAtRef.current >= 1000) {
      lastSavedAtRef.current = now;
      persistProgress({ currentTime: video.currentTime });
    }
  };

  const handleEnded = () => {
    const finalTime = effectiveDuration;
    setCurrentTime(finalTime);
    setIsPlaying(false);
    setCompleted(true);
    setView("complete");
    persistProgress({
      currentTime: finalTime,
      completed: true,
    });
    announce("Training completed.");
  };

  const handlePlaybackRateSelect = (
    event: ChangeEvent<HTMLSelectElement>,
  ) => {
    changePlaybackRate(Number(event.target.value));
  };

  const handleSeekInput = (event: ChangeEvent<HTMLInputElement>) => {
    seekTo(Number(event.target.value));
  };

  const handleVolumeInput = (event: ChangeEvent<HTMLInputElement>) => {
    changeVolume(Number(event.target.value));
  };

  const preventButtonSpacePropagation = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
  ) => {
    if (event.code === "Space") {
      event.stopPropagation();
    }
  };

  if (courseError) {
    return (
      <div className="app-shell">
        <SiteHeader completed={completed} />
        <main id="main-content" className="state-page" tabIndex={-1}>
          <section
            className="state-card"
            role="alert"
            data-testid="course-error"
          >
            <p className="eyebrow">Course unavailable</p>
            <h1>We could not load the training content</h1>
            <p>{courseError}</p>
            <button
              className="button button-primary"
              type="button"
              onClick={() => setManifestAttempt((attempt) => attempt + 1)}
              data-testid="retry-manifest"
            >
              Try again
            </button>
          </section>
        </main>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="app-shell">
        <SiteHeader completed={completed} />
        <main
          id="main-content"
          className="state-page"
          tabIndex={-1}
          data-testid="app-loading"
        >
          <section className="state-card" aria-labelledby="loading-title">
            <div className="loading-mark" aria-hidden="true" />
            <p className="eyebrow">Continuous training</p>
            <h1 id="loading-title">Preparing the course</h1>
            <p role="status">Loading verified course metadata…</p>
          </section>
        </main>
      </div>
    );
  }

  const currentModule = currentSlide?.module ?? course.modules[0]?.title;

  return (
    <div className="app-shell">
      <SiteHeader completed={completed} />

      <main id="main-content" tabIndex={-1}>
        <section
          className="overview-page"
          aria-labelledby="course-title"
          hidden={view !== "overview"}
          data-testid="landing-page"
        >
          <div className="overview-hero">
            <div className="overview-copy">
              <p className="eyebrow">Continuous narrated course</p>
              <h1
                id="course-title"
                ref={overviewHeadingRef}
                tabIndex={-1}
                data-testid="course-title"
              >
                {course.courseTitle}
              </h1>
              <p className="course-subtitle">{course.subtitle}</p>
              <div className="candidate-block">
                <strong>{course.candidate}</strong>
                <span>{course.credential}</span>
              </div>

              <dl className="course-facts" aria-label="Course facts">
                <div>
                  <dt>Slides</dt>
                  <dd data-testid="slide-count">{course.totalSlides}</dd>
                </div>
                <div>
                  <dt>Duration</dt>
                  <dd data-testid="course-duration">
                    {formatDurationLabel(course.totalDurationMs)}
                  </dd>
                </div>
                <div>
                  <dt>Modules</dt>
                  <dd data-testid="module-count">
                    {course.modules.length}
                  </dd>
                </div>
              </dl>

              <div className="launch-panel">
                <button
                  className="button button-primary button-launch"
                  type="button"
                  onClick={() => void startPlayback(0, true)}
                  onKeyDown={preventButtonSpacePropagation}
                  disabled={!mediaReady || !!mediaError}
                  data-testid="play-training"
                >
                  <span aria-hidden="true">▶</span>
                  Play Training
                </button>
                <span className="launch-note">
                  Playback begins only after you select Play Training.
                </span>
              </div>

              {hasResume && (
                <aside
                  className="resume-card"
                  aria-labelledby="resume-title"
                  data-testid="resume-prompt"
                >
                  <div>
                    <p className="status-kicker" id="resume-title">
                      Saved on this device
                    </p>
                    <p>
                      Resume at {formatClock(savedProgress.currentTime)} ·
                      slide{" "}
                      {
                        course.slides[
                          findSlideIndex(
                            course.slides,
                            savedProgress.currentTime,
                          )
                        ].id
                      }
                    </p>
                  </div>
                  <div className="resume-actions">
                    <button
                      className="button button-secondary"
                      type="button"
                      onClick={() =>
                        void startPlayback(savedProgress.currentTime)
                      }
                      disabled={!mediaReady || !!mediaError}
                      data-testid="resume-training"
                    >
                      Resume Training
                    </button>
                    <button
                      className="button button-quiet"
                      type="button"
                      onClick={() => void startPlayback(0, true)}
                      disabled={!mediaReady || !!mediaError}
                      data-testid="start-again"
                    >
                      Start Again
                    </button>
                  </div>
                </aside>
              )}

              {!mediaReady && !mediaError && (
                <p className="media-status" role="status">
                  <span className="status-dot" aria-hidden="true" />
                  Preparing the secure video stream…
                </p>
              )}

              {mediaError && (
                <div
                  className="inline-error"
                  role="alert"
                  data-testid="media-error-overview"
                >
                  <strong>Playback is not ready.</strong>
                  <span>{mediaError}</span>
                  <button
                    className="button button-secondary"
                    type="button"
                    onClick={() =>
                      setMediaAttempt((attempt) => attempt + 1)
                    }
                    data-testid="retry-media-overview"
                  >
                    Retry video
                  </button>
                </div>
              )}
            </div>

            <figure className="course-poster">
              <img
                src={course.continuous.poster}
                alt={course.slides[0].altText}
                width="1920"
                height="1080"
              />
              <figcaption>
                Original presentation visuals and embedded narration
              </figcaption>
            </figure>
          </div>

          <div className="overview-details">
            <section
              className="objectives-panel"
              aria-labelledby="objectives-title"
              data-testid="learning-objectives"
            >
              <p className="section-number">01</p>
              <div>
                <p className="eyebrow">Learning outcomes</p>
                <h2 id="objectives-title">What you will be able to explain</h2>
                <ul className="objective-list">
                  {course.learningObjectives.map((objective) => (
                    <li key={objective}>{objective}</li>
                  ))}
                </ul>
              </div>
            </section>

            <section
              className="modules-panel"
              aria-labelledby="modules-title"
              data-testid="module-outline"
            >
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Course outline</p>
                  <h2 id="modules-title">Six modules, one continuous lesson</h2>
                </div>
                <p>
                  The original 17-slide sequence is preserved from beginning
                  to conclusion.
                </p>
              </div>
              <ol className="module-grid">
                {course.modules.map((module) => (
                  <li key={module.id}>
                    <span className="module-index">
                      {String(module.id).padStart(2, "0")}
                    </span>
                    <h3>{module.title.replace(/^Module \d+ — /, "")}</h3>
                    <p>
                      {module.slideStart === module.slideEnd
                        ? `Slide ${module.slideStart}`
                        : `Slides ${module.slideStart}–${module.slideEnd}`}
                    </p>
                    {completed && (
                      <span className="module-complete">
                        <span aria-hidden="true">✓</span> Complete
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          </div>
        </section>

        <section
          className="player-page"
          aria-labelledby="player-title"
          hidden={view !== "player"}
          data-testid="player-page"
        >
          <div className="player-heading-row">
            <button
              className="button button-quiet overview-button"
              type="button"
              onClick={returnToOverview}
              data-testid="course-overview-button"
            >
              ← Course overview
            </button>
            <div>
              <p className="eyebrow">Continuous narrated course</p>
              <h1
                id="player-title"
                ref={playerHeadingRef}
                tabIndex={-1}
              >
                {course.courseTitle}
              </h1>
            </div>
          </div>

          <div
            className={`learning-workspace ${
              transcriptOpen ? "has-transcript" : ""
            }`}
          >
            <div className="player-column">
              <div
                className="player-shell"
                ref={playerShellRef}
                data-testid="player-shell"
              >
                <div className="video-frame">
                  <video
                    ref={videoRef}
                    className="course-video"
                    poster={course.continuous.poster}
                    preload="metadata"
                    playsInline
                    crossOrigin="anonymous"
                    aria-label={`${course.courseTitle} continuous training video`}
                    data-testid="course-video"
                    onCanPlay={() => {
                      setMediaReady(true);
                      setIsBuffering(false);
                    }}
                    onDurationChange={(event) => {
                      if (Number.isFinite(event.currentTarget.duration)) {
                        setDuration(event.currentTarget.duration);
                      }
                    }}
                    onTimeUpdate={handleTimeUpdate}
                    onSeeking={() => setIsBuffering(true)}
                    onSeeked={() => setIsBuffering(false)}
                    onWaiting={() => setIsBuffering(true)}
                    onStalled={() => setIsBuffering(true)}
                    onPlaying={() => {
                      setIsBuffering(false);
                      setIsPlaying(true);
                      setMediaError(null);
                      announce("Training playing.");
                    }}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => {
                      setIsPlaying(false);
                      persistProgress();
                      if (view === "player") {
                        announce("Training paused.");
                      }
                    }}
                    onEnded={handleEnded}
                    onError={() => {
                      if (videoRef.current?.error) {
                        setMediaError(
                          "The browser reported a media playback error. Retry the video or use another current browser.",
                        );
                      }
                    }}
                  >
                    <track
                      kind="captions"
                      src={course.continuous.caption}
                      srcLang="en"
                      label="English"
                      onLoad={syncCaptionMode}
                    />
                  </video>

                  {currentTime <= 0.1 && !mediaError && (
                    <img
                      className="video-poster-fallback"
                      src={course.continuous.poster}
                      alt=""
                      aria-hidden="true"
                    />
                  )}

                  {isBuffering && !mediaError && (
                    <div
                      className="video-state-overlay"
                      role="status"
                      data-testid="buffering-indicator"
                    >
                      <span className="buffering-ring" aria-hidden="true" />
                      Buffering training…
                    </div>
                  )}

                  {mediaError && (
                    <div
                      className="video-state-overlay error-overlay"
                      role="alert"
                      data-testid="media-error-player"
                    >
                      <strong>Playback interrupted</strong>
                      <span>{mediaError}</span>
                      <button
                        className="button button-light"
                        type="button"
                        onClick={() =>
                          setMediaAttempt((attempt) => attempt + 1)
                        }
                        data-testid="retry-media-player"
                      >
                        Retry video
                      </button>
                    </div>
                  )}
                </div>

                <div className="slide-context">
                  <div>
                    <span data-testid="current-slide">
                      Slide {currentSlide?.id ?? 1} of {course.totalSlides}
                    </span>
                    <strong>{currentSlide?.title}</strong>
                  </div>
                  <span data-testid="current-module">{currentModule}</span>
                </div>

                <div
                  className="custom-controls"
                  aria-label="Training playback controls"
                >
                  <div className="timeline-control">
                    <label className="sr-only" htmlFor="course-seek">
                      Seek through course
                    </label>
                    <input
                      id="course-seek"
                      className="range range-seek"
                      type="range"
                      min="0"
                      max={effectiveDuration || 0}
                      step="0.1"
                      value={clamp(
                        currentTime,
                        0,
                        effectiveDuration || 0,
                      )}
                      onChange={handleSeekInput}
                      aria-valuetext={`${formatClock(
                        currentTime,
                      )} of ${formatClock(effectiveDuration)}`}
                      style={{
                        "--range-progress": `${progressPercent}%`,
                      } as React.CSSProperties}
                      data-testid="seek-bar"
                    />
                    <div className="time-display" aria-live="off">
                      <time>{formatClock(currentTime)}</time>
                      <span aria-hidden="true">/</span>
                      <time>{formatClock(effectiveDuration)}</time>
                    </div>
                  </div>

                  <div className="control-rows">
                    <div className="primary-controls">
                      <button
                        className="control-button skip-control"
                        type="button"
                        onClick={() => skipBy(-10)}
                        aria-label="Rewind 10 seconds"
                        aria-keyshortcuts="ArrowLeft"
                        data-testid="rewind-10"
                      >
                        <span aria-hidden="true">↶</span> 10
                      </button>
                      <button
                        className="control-button play-control"
                        type="button"
                        onClick={() => void togglePlayback()}
                        aria-label={isPlaying ? "Pause training" : "Play training"}
                        aria-keyshortcuts="Space"
                        data-testid="play-pause"
                      >
                        <span aria-hidden="true">
                          {isPlaying ? "Ⅱ" : "▶"}
                        </span>
                        {isPlaying ? "Pause" : "Play"}
                      </button>
                      <button
                        className="control-button skip-control"
                        type="button"
                        onClick={() => skipBy(10)}
                        aria-label="Fast-forward 10 seconds"
                        aria-keyshortcuts="ArrowRight"
                        data-testid="forward-10"
                      >
                        10 <span aria-hidden="true">↷</span>
                      </button>
                    </div>

                    <div className="utility-controls">
                      <div className="volume-control">
                        <button
                          className="control-button icon-control"
                          type="button"
                          onClick={toggleMute}
                          aria-label={muted ? "Unmute audio" : "Mute audio"}
                          aria-pressed={muted}
                          aria-keyshortcuts="M"
                          data-testid="mute-toggle"
                        >
                          <span aria-hidden="true">
                            {muted || volume === 0 ? "Muted" : "Volume"}
                          </span>
                        </button>
                        <label className="sr-only" htmlFor="volume">
                          Volume
                        </label>
                        <input
                          id="volume"
                          className="range range-volume"
                          type="range"
                          min="0"
                          max="1"
                          step="0.05"
                          value={muted ? 0 : volume}
                          onChange={handleVolumeInput}
                          aria-valuetext={`${Math.round(
                            (muted ? 0 : volume) * 100,
                          )} percent`}
                          data-testid="volume-control"
                        />
                      </div>

                      <label className="speed-control">
                        <span className="sr-only">Playback speed</span>
                        <select
                          value={playbackRate}
                          onChange={handlePlaybackRateSelect}
                          aria-label="Playback speed"
                          data-testid="playback-speed"
                        >
                          {PLAYBACK_SPEEDS.map((speed) => (
                            <option key={speed} value={speed}>
                              {speed}×
                            </option>
                          ))}
                        </select>
                      </label>

                      <button
                        className="control-button"
                        type="button"
                        onClick={toggleCaptions}
                        aria-pressed={captionsEnabled}
                        aria-keyshortcuts="C"
                        data-testid="captions-toggle"
                      >
                        CC {captionsEnabled ? "On" : "Off"}
                      </button>
                      <button
                        className="control-button"
                        type="button"
                        onClick={toggleTranscript}
                        aria-pressed={transcriptOpen}
                        data-testid="transcript-toggle"
                      >
                        Transcript
                      </button>
                      <button
                        className="control-button"
                        type="button"
                        onClick={() => void toggleFullscreen()}
                        aria-keyshortcuts="F"
                        data-testid="fullscreen-toggle"
                      >
                        Fullscreen
                      </button>
                      <button
                        className="control-button"
                        type="button"
                        onClick={restartTraining}
                        aria-keyshortcuts="R"
                        data-testid="restart-training"
                      >
                        Restart
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <section
                className="course-progress-card"
                aria-labelledby="progress-heading"
              >
                <div>
                  <p className="eyebrow">Course progress</p>
                  <h2 id="progress-heading">{currentModule}</h2>
                </div>
                <div className="progress-figure">
                  <strong data-testid="progress-percent">
                    {Math.floor(progressPercent)}%
                  </strong>
                  <span>completed</span>
                </div>
                <progress
                  max="100"
                  value={progressPercent}
                  aria-label={`${Math.floor(progressPercent)} percent completed`}
                >
                  {Math.floor(progressPercent)}%
                </progress>
              </section>
            </div>

            {transcriptOpen && (
              <aside
                className="transcript-panel"
                aria-labelledby="transcript-heading"
                data-testid="transcript-panel"
              >
                <div className="transcript-header">
                  <div>
                    <p className="eyebrow">Speaker notes</p>
                    <h2 id="transcript-heading">Course transcript</h2>
                  </div>
                  <button
                    className="close-transcript"
                    type="button"
                    onClick={toggleTranscript}
                    aria-label="Close transcript"
                  >
                    ×
                  </button>
                </div>

                <label className="transcript-search">
                  <span>Search transcript</span>
                  <input
                    type="search"
                    value={transcriptQuery}
                    onChange={(event) =>
                      setTranscriptQuery(event.target.value)
                    }
                    placeholder="Search terms or slide titles"
                    data-testid="transcript-search"
                  />
                </label>

                <p className="search-summary" role="status">
                  {transcriptQuery.trim()
                    ? `${filteredSlides.length} matching ${
                        filteredSlides.length === 1 ? "slide" : "slides"
                      }`
                    : `Following slide ${currentSlide?.id ?? 1}`}
                </p>

                <div
                  className="transcript-sections"
                  role="region"
                  aria-label="Course transcript sections"
                  tabIndex={0}
                >
                  {filteredSlides.map((slide) => {
                    const isCurrent = slide.id === currentSlide?.id;
                    return (
                      <article
                        key={slide.id}
                        ref={isCurrent ? activeTranscriptRef : undefined}
                        className={`transcript-section ${
                          isCurrent ? "is-current" : ""
                        }`}
                        aria-current={isCurrent ? "true" : undefined}
                        data-testid={`transcript-slide-${slide.id}`}
                        data-current={isCurrent ? "true" : "false"}
                      >
                        <div className="transcript-slide-heading">
                          <span>Slide {slide.id}</span>
                          {isCurrent && <em>Now playing</em>}
                        </div>
                        <h3>{slide.title}</h3>
                        <p>{slide.transcript}</p>
                      </article>
                    );
                  })}

                  {filteredSlides.length === 0 && (
                    <div className="no-results">
                      <strong>No transcript matches</strong>
                      <p>Try another word or clear the search field.</p>
                    </div>
                  )}
                </div>
              </aside>
            )}
          </div>
        </section>

        <section
          className="completion-page"
          aria-labelledby="completion-title"
          hidden={view !== "complete"}
          data-testid="completion-screen"
        >
          <div className="completion-card">
            <span className="completion-mark" aria-hidden="true">
              ✓
            </span>
            <p className="eyebrow">Course record updated</p>
            <h1
              id="completion-title"
              ref={completionHeadingRef}
              tabIndex={-1}
            >
              Training completed
            </h1>
            <p className="completion-course-title">{course.courseTitle}</p>
            <blockquote>“{course.conclusion}”</blockquote>

            <div className="completion-modules">
              <h2>Six modules completed</h2>
              <ol>
                {course.modules.map((module) => (
                  <li key={module.id}>
                    <span aria-hidden="true">✓</span>
                    {module.title}
                  </li>
                ))}
              </ol>
            </div>

            <div className="completion-actions">
              <button
                className="button button-primary"
                type="button"
                onClick={() => void startPlayback(0, true)}
                data-testid="replay-course"
              >
                Replay course
              </button>
              <button
                className="button button-secondary"
                type="button"
                onClick={() => {
                  videoRef.current?.pause();
                  setCurrentTime(Math.max(effectiveDuration - 0.1, 0));
                  setTranscriptOpen(true);
                  setTranscriptQuery("");
                  setView("player");
                }}
                data-testid="review-transcript"
              >
                Review transcript
              </button>
              <button
                className="button button-quiet"
                type="button"
                onClick={returnToOverview}
                data-testid="return-overview"
              >
                Return to course overview
              </button>
            </div>
          </div>
        </section>
      </main>

      <div
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
        data-testid="live-region"
      >
        {liveMessage}
      </div>
    </div>
  );
}

function SiteHeader({ completed }: { completed: boolean }) {
  return (
    <header className="site-header">
      <div className="wordmark" aria-label="Adaptive AI Governance Training">
        <span className="wordmark-mark" aria-hidden="true">
          A
        </span>
        <span>
          <strong>Adaptive AI Governance</strong>
          <small>Postgraduate training</small>
        </span>
      </div>
      <div
        className={`device-status ${completed ? "is-complete" : ""}`}
        data-testid="course-status"
      >
        <span aria-hidden="true" />
        {completed ? "Completed on this device" : "Progress saved on this device"}
      </div>
    </header>
  );
}

export default App;
